import { api } from "./api.service";
import { toast } from "sonner";
import { store } from "../redux/store";
import { setUsersList } from "../store/usersListSlice";
import type { IUser } from "../interfaces";

async function usersList(): Promise<any> {
  try {
    const { data } = await api.get(`/user/find`);

    store.dispatch(setUsersList(data));
    return data;
  } catch (error: any) {
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;

    toast.error("ops, tivemos um problema para enviar as informações! tente novamente");

    console.log("Erro", erro);
    return;
  }
}

interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role?: string;
  // Dados de perfil opcionais, alinhados ao UserProfileDto do backend
  profileDraft?: {
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    birthDay?: string;
    registrationNumber?: string;
    phone?: string;
    socialProgram?: boolean;
    programName?: string;
  };
  // Dados específicos de cada role (SECRETARY, FINANCIAL, DOCTOR, etc.)
  roleData?: any;
}

async function createUser(payload: CreateUserPayload): Promise<any> {
  try {
    // Rota protegida: /user/create-with-role cria usuários com role específica
    // Requer JWT e respeita as regras de autorização e roles do backend.
    const { data } = await api.post(`/user/create-with-role`, payload);
    return data;
  } catch (error: any) {
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;

    toast.error("Não foi possível criar o usuário. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

// Busca paciente por CPF (somente usuários role USER com perfil que tenha registrationNumber)
async function findUserByCpf(cpf: string): Promise<any | null> {
  try {
    const onlyNumbers = cpf.replace(/\D/g, "");
    const { data } = await api.get(`/user/find-by-cpf/${onlyNumbers}`);
    return data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;
    toast.error("Não foi possível buscar o paciente. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

// Busca usuário genérico por ID (paciente, médico, etc.)
async function getUserById(id: string): Promise<IUser | null> {
  try {
    const { data } = await api.get<IUser>(`/user/${id}`);
    return data ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;
    toast.error("Não foi possível carregar o usuário. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

// Atualiza a lista de médicos vinculados a uma secretária específica
async function linkDoctorsToSecretary(secretaryUserId: string, doctorIds: string[]): Promise<void> {
  try {
    await api.patch(`/user/${secretaryUserId}/link-doctors`, { doctorIds });
  } catch (error: any) {
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;
    toast.error("Não foi possível atualizar os médicos vinculados. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

// Ativa/desativa usuário (campo isActive)
async function updateUserIsActive(userId: string, isActive: boolean): Promise<any> {
  try {
    const { data } = await api.patch(`/user/${userId}`, { isActive });
    // Recarrega a lista para refletir a mudança
    await usersList();
    return data;
  } catch (error: any) {
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;

    toast.error("Não foi possível atualizar o status do usuário. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

export interface AdminUpdateUserPayload {
  name?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  password?: string;
  confirmPassword?: string;
}

// ADMIN: atualiza campos do usuário selecionado
async function adminUpdateUser(userId: string, payload: AdminUpdateUserPayload): Promise<any> {
  try {
    const { data } = await api.patch(`/user/${userId}`, payload);
    await usersList();
    return data;
  } catch (error: any) {
    const erro = error.response
      ? `
      ${error?.response?.data?.message}  
      ${error?.response?.status}`
      : error.message;

    toast.error("Não foi possível editar o usuário. Tente novamente.");
    console.log("Erro", erro);
    throw error;
  }
}

export {
  usersList,
  createUser,
  findUserByCpf,
  getUserById,
  linkDoctorsToSecretary,
  updateUserIsActive,
  adminUpdateUser,
};
